import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useConfirmation } from '@/components/shared/ZCard';
import { ArrowLeft, Plus, Trash2, Edit2, X, ChevronRight, ExternalLink, GripVertical, Image, Upload, FileDown, Link2 } from 'lucide-react';
import ZTabs from '@/components/shared/ZTabs';
import OverflowMenu from '@/components/shared/OverflowMenu';
import Logotype from '@/components/shared/Logotype';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const THEME_COLORS = {
  PURPOSE: '#c9a84c', INTEGRATION: '#7eb8c9', HARDSHIP: '#b87d7d', HONESTY: '#c97eb8',
  FREEDOM: '#7ec9a0', DISCIPLINE: '#c98e7e', ALIVENESS: '#a07ec9', SOVEREIGNTY: '#7e9ec9',
  DISCERNMENT: '#c9c47e', LOVE: '#c97e8f', AUTHENTICITY: '#7ec9c4', AGENCY: '#c9b47e',
  INDIFFERENCE: '#9ec97e', NARRATIVE: '#8f7ec9', 'THE THINKER': '#e8e2d9',
};

const PAINTINGS = [
  { id: 'diagonal', url: '/paintings/diagonal.jpg', mood: 'silver' },
  { id: 'cross-silver', url: '/paintings/cross-silver.jpg', mood: 'silver' },
  { id: 'chaos', url: '/paintings/chaos.jpg', mood: 'monochrome' },
  { id: '777', url: '/paintings/777.jpg', mood: 'gold' },
  { id: 'explosion', url: '/paintings/explosion.jpg', mood: 'vibrant' },
  { id: 'x-blue', url: '/paintings/x-blue.jpg', mood: 'silver' },
  { id: 'x-gold', url: '/paintings/x-gold.jpg', mood: 'gold' },
  { id: 'field', url: '/paintings/field.jpg', mood: 'gold' },
];

const getDaysSeed = () => {
  const now = new Date();
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

const seededShuffle = (arr, seed) => {
  const shuffled = [...arr];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const PAINTING_STYLES = [
  { type: 'card', className: 'col-span-1', aspectRatio: 'aspect-[4/3]' },
  { type: 'wide', className: 'md:col-span-2', aspectRatio: 'aspect-[16/7]' },
  { type: 'accent', className: 'col-span-1', aspectRatio: 'aspect-square', opacity: 0.6 },
  { type: 'separator', className: 'md:col-span-3', aspectRatio: 'aspect-[21/6]' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function PaintingVisual({ painting, style }) {
  if (style.type === 'separator') {
    return (
      <div className={`${style.className} overflow-hidden group`}>
        <div className={`${style.aspectRatio} relative`}>
          <img src={painting.url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-[1.02] transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
        </div>
      </div>
    );
  }
  if (style.type === 'accent') {
    return (
      <div className={`${style.className} bg-white/[0.02] border border-white/[0.06] overflow-hidden relative group transition-all duration-300 hover:border-[#b0b4c0] hover:scale-[1.02]`}>
        <div className={`${style.aspectRatio}`}>
          <img src={painting.url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-all duration-700 grayscale group-hover:grayscale-0 group-hover:scale-[1.02]" />
        </div>
      </div>
    );
  }
  return (
    <div className={`${style.className} bg-[#0a0a0f] overflow-hidden group transition-all duration-300 hover:scale-[1.02]`}>
      <div className={`${style.aspectRatio} relative`}>
        <img src={painting.url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700 group-hover:scale-[1.02]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </div>
  );
}

function SortableMantraCard({ mantra, onOpen, onEdit, onDelete, onTogglePublic, isDragging }) {
  const tc = THEME_COLORS[mantra.theme] || '#b0b4c0';
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: mantra.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, scale: isDragging ? 1.02 : 1 };

  return (
    <div ref={setNodeRef} style={{ ...style, position: 'relative' }} {...attributes} {...listeners} data-testid={`mantra-card-${mantra.id}`}
      className="text-left bg-white/[0.02] border border-white/[0.06] p-6 md:p-7 overflow-hidden group transition-all duration-300 hover:bg-white/[0.04] hover:border-[#b0b4c0] hover:scale-[1.02] cursor-grab active:cursor-grabbing">
      <div onPointerDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
        <OverflowMenu
          publishToggle={{ isPublished: mantra.is_public, onToggle: () => onTogglePublic(mantra) }}
          items={[
            { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => onEdit(mantra) },
            { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(mantra), destructive: true },
          ]}
        />
      </div>
      <button onClick={() => onOpen(mantra.id)} className="text-left w-full">
        <div className="absolute top-0 left-0 right-0 h-[2px] transition-transform duration-400 origin-left scale-x-0 group-hover:scale-x-100" style={{ background: tc }} />
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: tc, opacity: 0.3 }} />
        <div className="flex justify-between items-start mb-4">
          <span className="text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: tc, opacity: 0.8 }}>{mantra.theme}</span>
        </div>
        <p className="text-base leading-[1.7] mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>{mantra.title || mantra.text || 'Untitled'}</p>
        {mantra.subtitle && <p className="text-xs italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>{mantra.subtitle}</p>}
        <div className="mt-5 flex items-center gap-2">
          <span className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>{(mantra.quotes || []).length} {(mantra.quotes || []).length === 1 ? 'voice' : 'voices'}</span>
          <ChevronRight size={10} className="text-[#b0b4c0] group-hover:text-[#6b7084] transition-colors" />
        </div>
      </button>
    </div>
  );
}

function PaintingUploadDialog({ open, onClose, onSave }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFiles = async (fileList) => {
    const imgs = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setFiles(prev => [...prev, ...imgs]);
    const newPreviews = await Promise.all(imgs.map(f => new Promise(r => {
      const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(f);
    })));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      await Promise.all(files.map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const { data: uploadData } = await api.post('/upload', fd);
        await api.post('/cult/paintings', { url: uploadData.url, title: '', is_public: false });
      }));
      setFiles([]); setPreviews([]);
      onSave();
      onClose();
    } catch {}
    finally { setUploading(false); }
  };

  const reset = () => { setFiles([]); setPreviews([]); onClose(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) reset(); }}>
      <DialogContent className="bg-white/[0.02] border-white/[0.06] text-[#f0f0f5] max-w-md" aria-describedby="painting-upload-desc">
        <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add Paintings</DialogTitle></DialogHeader>
        <p id="painting-upload-desc" className="sr-only">Upload paintings</p>
        <div className="space-y-4 mt-2">
          <div className="border-2 border-dashed border-white/[0.06] rounded-lg p-8 text-center cursor-pointer hover:border-[#6B35B8]/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            onDragOver={e => e.preventDefault()}>
            <Upload className="mx-auto mb-2 text-[#6b7084]" size={24} />
            <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>Drop images here or tap to select</p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
          </div>
          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
              {previews.map((p, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden bg-white/[0.03]">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full text-white/60 hover:text-white"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={upload} disabled={!files.length || uploading} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full text-xs tracking-wider">
            {uploading ? 'Uploading…' : `Add ${files.length || ''} painting${files.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SortablePaintingCard({ painting, onEdit, onDelete, onTogglePublic, onView }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: painting.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 100 : 1, position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className="bg-white/[0.02] border border-white/[0.06] overflow-hidden group hover:border-[#b0b4c0] transition-all duration-300 cursor-grab active:cursor-grabbing touch-manipulation">
      <OverflowMenu
        publishToggle={{ isPublished: painting.is_public, onToggle: () => onTogglePublic(painting) }}
        items={[
          { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => onEdit(painting) },
          { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(painting), destructive: true },
        ]}
      />
      {/* Drag hint - visual only */}
      <div className="absolute top-2 left-2 z-10 p-1 text-[#b0b4c0]/30 group-hover:text-[#b0b4c0]/70 transition-colors pointer-events-none">
        <GripVertical size={14} />
      </div>
      {painting.url ? (
        <div className="aspect-square relative cursor-pointer" onClick={(e) => { e.stopPropagation(); if (onView) onView(painting); }}><img src={painting.url} alt={painting.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" /></div>
      ) : (
        <div className="aspect-square bg-[#0a0a0f] flex items-center justify-center pointer-events-none"><Image size={32} className="text-white/[0.06]" /></div>
      )}
      <div className="p-3">
        <p className="text-sm text-[#f0f0f5] truncate pointer-events-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{painting.title || 'Untitled'}</p>
      </div>
    </div>
  );
}

export default function CultOfOne() {
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [searchParams] = useSearchParams();

  // Tab state — honor ?tab= query param so breadcrumbs from nested editors
  // (e.g. CuratedEditor) can land on the right tab.
  const initialTab = (() => {
    const t = searchParams.get('tab');
    const known = ['mantras', 'essays', 'poems', 'paintings', 'curated'];
    return known.includes(t) ? t : 'mantras';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Mantra state
  const [mantras, setMantras] = useState([]);
  const [active, setActive] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ text: '', author: '' });
  const [editingMantra, setEditingMantra] = useState(null);
  const [savingMantra, setSavingMantra]   = useState(false);
  const [mantraForm, setMantraForm] = useState({ title: '', theme: '', subtitle: '', notes: '', attribution: '', quotes: [] });
  const [newVoiceText, setNewVoiceText] = useState('');
  const [newVoiceAuthor, setNewVoiceAuthor] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });
  const [activeId, setActiveId] = useState(null);

  // Essay state
  const [essays, setEssays] = useState([]);
  const [editingEssay, setEditingEssay] = useState(null);
  const [essayForm, setEssayForm] = useState({ title: '', tagline: '', body: '', is_public: false });

  // Poem state
  const [poems, setPoems] = useState([]);
  const [editingPoem, setEditingPoem] = useState(null);
  const [poemForm, setPoemForm] = useState({ title: '', body: '', is_public: false });

  // Paintings state
  const [cultPaintings, setCultPaintings] = useState([]);
  const [editingPainting, setEditingPainting] = useState(null);
  const [viewingPainting, setViewingPainting] = useState(null);
  const [paintingTitleForm, setPaintingTitleForm] = useState('');
  const [paintingActiveId, setPaintingActiveId] = useState(null);
  const [showPaintingUpload, setShowPaintingUpload] = useState(false);
  const [curatedGuides, setCuratedGuides] = useState([]);
  const [showNewGuide, setShowNewGuide] = useState(false);
  const [newGuideTitle, setNewGuideTitle] = useState('');
  const [creatingGuide, setCreatingGuide] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [guideEditForm, setGuideEditForm] = useState({ title: '', subtitle: '' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { loadMantras(); }, []);

  useEffect(() => {
    if (activeTab === 'essays') loadEssays();
    if (activeTab === 'poems') loadPoems();
    if (activeTab === 'paintings') loadCultPaintings();
    if (activeTab === 'curated' && curatedGuides.length === 0) {
      api.get('/curated').then(r => setCuratedGuides(r.data || [])).catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.app === 'cult') loadMantras(); };
    window.addEventListener('universal-input-applied', handler);
    return () => window.removeEventListener('universal-input-applied', handler);
  }, []);

  const loadMantras = async () => {
    try { const { data } = await api.get('/mantras'); setMantras(Array.isArray(data) ? data : []); } catch {}
  };

  const loadEssays = async () => {
    try { const { data } = await api.get('/essays'); setEssays(data); } catch {}
  };

  const loadPoems = async () => {
    try { const { data } = await api.get('/poems'); setPoems(data); } catch {}
  };

  const createGuide = async () => {
    if (!newGuideTitle.trim()) return;
    setCreatingGuide(true);
    try {
      const { data } = await api.post('/curated', { title: newGuideTitle.trim() });
      setCuratedGuides(prev => [...prev, data]);
      setShowNewGuide(false);
      setNewGuideTitle('');
      navigate(`/curated/${data.slug}`);
    } catch (e) {
      feedback.fail();
    }
    setCreatingGuide(false);
  };

  const toggleGuidePublic = async (g) => {
    const newVal = !g.is_public;
    setCuratedGuides(prev => prev.map(x => x.slug === g.slug ? { ...x, is_public: newVal } : x));
    try { await api.put(`/curated/${g.slug}`, { is_public: newVal }); } catch {
      setCuratedGuides(prev => prev.map(x => x.slug === g.slug ? { ...x, is_public: !newVal } : x));
    }
  };

  const startEditGuide = (g) => {
    setEditingGuide(g.slug);
    setGuideEditForm({ title: g.title || '', subtitle: g.subtitle || '' });
  };

  const saveGuideEdit = async () => {
    if (!editingGuide) return;
    try {
      await api.put(`/curated/${editingGuide}`, guideEditForm);
      setCuratedGuides(prev => prev.map(x => x.slug === editingGuide ? { ...x, ...guideEditForm } : x));
      setEditingGuide(null);
    } catch {}
  };

  const deleteGuide = async (slug) => {
    try {
      await api.delete(`/curated/${slug}`);
      setCuratedGuides(prev => prev.filter(x => x.slug !== slug));
    } catch {}
  };

  const current = mantras.find(m => m.id === active);
  const color = current ? (THEME_COLORS[current.theme] || '#b0b4c0') : '#b0b4c0';

  const openMantra = (id) => { setActive(id); window.scrollTo({ top: 0, behavior: 'instant' }); };

  const togglePublic = async (m) => {
    const newVal = !m.is_public;
    setMantras(prev => prev.map(x => x.id === m.id ? { ...x, is_public: newVal } : x));
    try { await api.put(`/mantras/${m.id}`, { ...m, is_public: newVal }); } catch { setMantras(prev => prev.map(x => x.id === m.id ? { ...x, is_public: !newVal } : x)); feedback.fail(); }
  };

  const removeMantra = async (id) => {
    try { await api.delete(`/mantras/${id}`); if (active === id) setActive(null); loadMantras(); feedback.confirm(); } catch { feedback.fail(); }
  };

  const confirmDeleteMantra = (m) => setDeleteConfirm({ open: true, type: 'mantra', id: m.id, name: m.title || m.text || 'this mantra' });
  const confirmDeleteQuote = (idx) => { const quote = current?.quotes?.[idx]; setDeleteConfirm({ open: true, type: 'quote', id: idx, name: quote?.text?.substring(0, 40) + '...' || 'this quote' }); };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'mantra') removeMantra(deleteConfirm.id);
    else if (deleteConfirm.type === 'quote') removeQuote(deleteConfirm.id);
    else if (deleteConfirm.type === 'essay') deleteEssay(deleteConfirm.id);
    else if (deleteConfirm.type === 'poem') deletePoem(deleteConfirm.id);
    else if (deleteConfirm.type === 'cult-painting') api.delete(`/cult/paintings/${deleteConfirm.id}`).then(() => { loadCultPaintings(); feedback.confirm(); }).catch(() => feedback.fail());
    else if (deleteConfirm.type === 'curated-guide') {
      deleteGuide(deleteConfirm.id);
    }
  };

  const startEditMantra = (m) => { setMantraForm({ title: m.title || m.text || '', theme: m.theme || '', subtitle: m.subtitle || '', notes: m.notes || '', attribution: m.attribution || '', quotes: m.quotes || [] }); setEditingMantra(m.id); };

  const saveMantraEdit = async () => {
    const m = mantras.find(x => x.id === editingMantra);
    if (!m) return;
    setSavingMantra(true);
    try {
      await api.put(`/mantras/${editingMantra}`, { ...m, ...mantraForm, text: mantraForm.title });
      await loadMantras();
      setEditingMantra(null);
      feedback.confirm();
    } catch { feedback.fail(); }
    setSavingMantra(false);
  };

  const addQuote = async () => {
    if (!quoteForm.text || !current) return;
    const quotes = [...(current.quotes || []), { text: quoteForm.text, author: quoteForm.author }];
    try { await api.put(`/mantras/${current.id}`, { ...current, quotes }); setQuoteForm({ text: '', author: '' }); setShowForm(false); loadMantras(); feedback.confirm(); } catch { feedback.fail(); }
  };

  const removeQuote = async (idx) => {
    if (!current) return;
    const quotes = current.quotes.filter((_, i) => i !== idx);
    try { await api.put(`/mantras/${current.id}`, { ...current, quotes }); loadMantras(); } catch { feedback.fail(); }
  };

  const saveQuoteEdit = async () => {
    if (!current || editingQuote === null) return;
    const quotes = [...current.quotes];
    quotes[editingQuote] = { ...quotes[editingQuote], text: quoteForm.text, author: quoteForm.author };
    try { await api.put(`/mantras/${current.id}`, { ...current, quotes }); setEditingQuote(null); setQuoteForm({ text: '', author: '' }); loadMantras(); } catch { feedback.fail(); }
  };

  const saveNewMantra = async () => {
    if (!mantraForm.title) return;
    try {
      await api.post('/mantras', { title: mantraForm.title, text: mantraForm.title, theme: mantraForm.theme, subtitle: mantraForm.subtitle, notes: mantraForm.notes, attribution: mantraForm.attribution, quotes: mantraForm.quotes, is_public: false, order: mantras.length + 1 });
      setEditingMantra(null); setMantraForm({ title: '', theme: '', subtitle: '', notes: '', attribution: '', quotes: [] }); setNewVoiceText(''); setNewVoiceAuthor(''); loadMantras(); feedback.confirm();
    } catch { feedback.fail(); }
  };

  // Essay functions
  const openEssay = (essay) => { setEditingEssay(essay); setEssayForm({ title: essay.title || '', tagline: essay.tagline || '', body: essay.body || '', is_public: essay.is_public || false }); };
  const openNewEssay = () => { setEditingEssay({ id: 'new' }); setEssayForm({ title: '', tagline: '', body: '', is_public: false }); };
  
  const saveEssay = async () => {
    if (!essayForm.title || !essayForm.body) return;
    try {
      if (editingEssay.id === 'new') {
        await api.post('/essays', essayForm);
        feedback.confirm();
      } else {
        await api.put(`/essays/${editingEssay.id}`, essayForm);
        feedback.confirm();
      }
      setEditingEssay(null); loadEssays();
    } catch { feedback.fail(); }
  };

  const deleteEssay = async (id) => {
    try { await api.delete(`/essays/${id}`); loadEssays(); feedback.confirm(); } catch { feedback.fail(); }
  };

  const toggleEssayPublic = async (essay) => {
    const newVal = !essay.is_public;
    setEssays(prev => prev.map(e => e.id === essay.id ? { ...e, is_public: newVal } : e));
    try { await api.put(`/essays/${essay.id}`, { ...essay, is_public: newVal }); } catch { setEssays(prev => prev.map(e => e.id === essay.id ? { ...e, is_public: !newVal } : e)); feedback.fail(); }
  };

  // Poem functions
  const openPoem = (poem) => { setEditingPoem(poem); setPoemForm({ title: poem.title || '', body: poem.body || '', is_public: poem.is_public || false }); };
  const openNewPoem = () => { setEditingPoem({ id: 'new' }); setPoemForm({ title: '', body: '', is_public: false }); };

  const savePoem = async () => {
    if (!poemForm.title || !poemForm.body) return;
    try {
      if (editingPoem.id === 'new') {
        await api.post('/poems', poemForm);
        feedback.confirm();
      } else {
        await api.put(`/poems/${editingPoem.id}`, poemForm);
        feedback.confirm();
      }
      setEditingPoem(null); loadPoems();
    } catch { feedback.fail(); }
  };

  const deletePoem = async (id) => {
    try { await api.delete(`/poems/${id}`); loadPoems(); feedback.confirm(); } catch { feedback.fail(); }
  };

  const togglePoemPublic = async (poem) => {
    const newVal = !poem.is_public;
    setPoems(prev => prev.map(p => p.id === poem.id ? { ...p, is_public: newVal } : p));
    try { await api.put(`/poems/${poem.id}`, { ...poem, is_public: newVal }); } catch { setPoems(prev => prev.map(p => p.id === poem.id ? { ...p, is_public: !newVal } : p)); feedback.fail(); }
  };

  // Paintings functions
  const loadCultPaintings = async () => {
    try { const { data } = await api.get('/cult/paintings'); setCultPaintings(data); } catch {}
  };


  const savePaintingTitle = async () => {
    if (!editingPainting) return;
    try { await api.put(`/cult/paintings/${editingPainting.id}`, { ...editingPainting, title: paintingTitleForm }); setEditingPainting(null); loadCultPaintings(); feedback.confirm(); } catch { feedback.fail(); }
  };

  const deleteCultPainting = async (painting) => {
    setDeleteConfirm({ open: true, type: 'cult-painting', id: painting.id, name: painting.title || 'this painting' });
  };

  const togglePaintingPublic = async (painting) => {
    const newVal = !painting.is_public;
    setCultPaintings(prev => prev.map(p => p.id === painting.id ? { ...p, is_public: newVal } : p));
    try { await api.put(`/cult/paintings/${painting.id}`, { ...painting, is_public: newVal }); } catch { setCultPaintings(prev => prev.map(p => p.id === painting.id ? { ...p, is_public: !newVal } : p)); feedback.fail(); }
  };

  const handlePaintingDragEnd = (event) => {
    const { active: a, over } = event;
    setPaintingActiveId(null);
    if (!over || a.id === over.id) return;
    const oldIndex = cultPaintings.findIndex(p => p.id === a.id);
    const newIndex = cultPaintings.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(cultPaintings, oldIndex, newIndex);
    setCultPaintings(reordered);
    api.post('/cult/paintings/reorder', { order: reordered.map(p => p.id) }).catch(() => { feedback.fail(); loadCultPaintings(); });
  };

  // Drag handlers
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    const { active: a, over } = event;
    setActiveId(null);
    if (!over || a.id === over.id) return;
    const oldIndex = mantras.findIndex(m => m.id === a.id);
    const newIndex = mantras.findIndex(m => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newMantras = arrayMove(mantras, oldIndex, newIndex);
    setMantras(newMantras);
    const updates = newMantras.map((m, idx) => m.order !== idx + 1 ? api.put(`/mantras/${m.id}`, { ...m, order: idx + 1 }) : null).filter(Boolean);
    if (updates.length > 0) Promise.all(updates).catch(() => { feedback.fail(); loadMantras(); });
  };

  const getSortingStrategy = () => typeof window !== 'undefined' && window.innerWidth >= 768 ? rectSortingStrategy : verticalListSortingStrategy;

  const daysSeed = getDaysSeed();
  const shuffledPaintings = seededShuffle(PAINTINGS, daysSeed);
  const shuffledStyles = seededShuffle(PAINTING_STYLES, daysSeed + 1);

  const buildGridItems = () => {
    const items = [];
    let paintingIdx = 0, styleIdx = 0;
    items.push({ type: 'painting', painting: shuffledPaintings[paintingIdx % shuffledPaintings.length], style: PAINTING_STYLES[3], key: 'painting-hero' });
    paintingIdx++;
    mantras.forEach((m, i) => {
      items.push({ type: 'mantra', mantra: m, key: m.id });
      if ((i + 1) % 3 === 0 && paintingIdx < shuffledPaintings.length + 2) {
        const style = shuffledStyles[styleIdx % shuffledStyles.length];
        items.push({ type: 'painting', painting: shuffledPaintings[paintingIdx % shuffledPaintings.length], style, key: `painting-${paintingIdx}` });
        paintingIdx++; styleIdx++;
      }
    });
    return items;
  };

  const gridItems = buildGridItems();

  // ==================== MANTRA DETAIL VIEW ====================
  if (active && current) {
    return (
      <div className="min-h-screen" data-testid="cult-detail" style={{ fontFamily: "'Georgia', serif" }}>
        <div className="px-6 md:px-10 pt-8 max-w-[760px]">
          <button data-testid="cult-back" onClick={() => setActive(null)} className="border border-white/[0.06] px-5 py-2.5 mb-12 text-xs tracking-[0.2em] uppercase hover:border-[#6b7084] hover:text-[#f0f0f5] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>All Mantras</button>
          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color, opacity: 0.8 }}>{current.theme}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-[1.3] mb-3 pl-6 border-l-4" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5', borderColor: color }} data-testid="mantra-detail-title">{current.title || current.text || 'Untitled'}</h2>
          {current.subtitle && <p className="text-base pl-6 italic mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>{current.subtitle}</p>}
          {current.attribution && <p className="text-sm pl-6 mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>— {current.attribution}</p>}

          <div className="mt-6 mb-10">
            <OverflowMenu
              publishToggle={{ isPublished: current.is_public, onToggle: () => togglePublic(current) }}
              triggerStyle={{ position: 'static' }}
              items={[
                { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => startEditMantra(current) },
                { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => confirmDeleteMantra(current), destructive: true },
              ]}
            />
          </div>

          <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>Voices</p>
          <div className="space-y-8" data-testid="mantra-quotes">
            {(current.quotes || []).map((q, i) => (
              <div key={i} data-testid={`quote-${i}`} className="pl-6 border-l-2 group relative" style={{ borderColor: color }}>
                {q.isPoem ? <p className="text-lg italic leading-[2.2] whitespace-pre-line" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0', fontSize: '1.25rem' }}>{q.text}</p> : <p className="text-xl sm:text-2xl italic leading-[1.6]" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>"{q.text}"</p>}
                <p className="mt-3 text-xs tracking-[0.15em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color, opacity: 0.7 }}>- {q.author}</p>
                <div className="absolute top-0 right-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-quote-${i}`} onClick={() => { setEditingQuote(i); setQuoteForm({ text: q.text, author: q.author }); }} className="p-1 text-[#b0b4c0] hover:text-[#b0b4c0] transition-colors"><Edit2 size={12} /></button>
                  <button data-testid={`delete-quote-${i}`} onClick={() => confirmDeleteQuote(i)} className="p-1 text-[#b0b4c0] hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>

          <button data-testid="add-voice-btn" onClick={() => { setQuoteForm({ text: '', author: '' }); setShowForm(true); }} className="mt-8 flex items-center gap-2 px-4 py-2 border border-dashed border-white/[0.06] hover:border-[#6b7084] transition-colors text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}><Plus size={12} /> Add Voice</button>

          {current.notes && <div className="mt-10 bg-white/[0.02] border border-white/[0.06] rounded p-4"><p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>Notes</p><p className="text-sm leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>{current.notes}</p></div>}

          <div className="flex justify-between mt-16 pb-12 pt-8 border-t border-white/[0.06]">
            {mantras.indexOf(current) > 0 ? <button onClick={() => openMantra(mantras[mantras.indexOf(current) - 1].id)} className="text-[12px] hover:text-[#f0f0f5] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>Prev</button> : <span />}
            {mantras.indexOf(current) < mantras.length - 1 ? <button onClick={() => openMantra(mantras[mantras.indexOf(current) + 1].id)} className="text-[12px] hover:text-[#f0f0f5] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>Next</button> : <span />}
          </div>
        </div>

        {/* Quote Dialogs */}
        <Dialog open={showForm || editingQuote !== null} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditingQuote(null); } }}>
          <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="quote-form-desc">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editingQuote !== null ? 'Edit Voice' : 'Add Voice'}</DialogTitle></DialogHeader>
            <p id="quote-form-desc" className="sr-only">Form to add or edit a quote</p>
            <div className="space-y-4 mt-2">
              <Textarea data-testid="quote-text-input" placeholder="The quote..." value={quoteForm.text} onChange={(e) => setQuoteForm(p => ({ ...p, text: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 min-h-[100px] italic" style={{ fontFamily: "'Playfair Display', serif" }} />
              <Input data-testid="quote-author-input" placeholder="Author" value={quoteForm.author} onChange={(e) => setQuoteForm(p => ({ ...p, author: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
              <Button data-testid="quote-save-btn" disabled={!quoteForm.text} onClick={editingQuote !== null ? saveQuoteEdit : addQuote} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">{editingQuote !== null ? 'Save' : 'Add'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Mantra Dialog */}
        <Dialog open={editingMantra !== null && editingMantra !== 'new'} onOpenChange={(v) => { if (!v) setEditingMantra(null); }}>
          <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="edit-mantra-desc">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Edit Mantra</DialogTitle></DialogHeader>
            <p id="edit-mantra-desc" className="sr-only">Edit mantra details</p>
            <div className="space-y-4 mt-2">
              <Textarea data-testid="edit-mantra-title" placeholder="The mantra..." value={mantraForm.title} onChange={(e) => setMantraForm(p => ({ ...p, title: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 min-h-[80px]" style={{ fontFamily: "'Playfair Display', serif" }} />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Theme" value={mantraForm.theme} onChange={(e) => setMantraForm(p => ({ ...p, theme: e.target.value.toUpperCase() }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs tracking-widest uppercase font-mono" />
                <Input placeholder="Subtitle" value={mantraForm.subtitle} onChange={(e) => setMantraForm(p => ({ ...p, subtitle: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs italic" />
              </div>
              <Input placeholder="Attribution (if not yours)" value={mantraForm.attribution} onChange={(e) => setMantraForm(p => ({ ...p, attribution: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
              <Textarea placeholder="Personal notes..." value={mantraForm.notes} onChange={(e) => setMantraForm(p => ({ ...p, notes: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 min-h-[60px] text-sm" />
              <Button onClick={saveMantraEdit} disabled={!mantraForm.title || savingMantra} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">
                {savingMantra ? <em>Saving...</em> : 'Save'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })} onConfirm={handleDeleteConfirm} itemName={deleteConfirm.name} itemType={deleteConfirm.type} />
      </div>
    );
  }

  // ==================== GRID VIEW ====================
  return (
    <div className="min-h-screen" data-testid="cult-page" style={{ fontFamily: "'Georgia', serif" }}>
      {/* Header */}
      <div className="border-b border-white/[0.06] px-6 md:px-10 pt-8 pb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center mb-1" style={{ gap: 2 }} data-testid="cult-title">
            <Logotype app="cult" size="header" />
          </div>
          <p className="text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#b0b4c0', paddingLeft: 42 }}>Manifest of a Movement</p>
        </div>
        <div className="text-right hidden md:block" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, color: '#b0b4c0', letterSpacing: 2 }}>
          <div>CONGRUENCE · EXPANSION</div>
          <div>ALIVENESS · SOVEREIGNTY</div>
        </div>
      </div>

      <div className="px-6 md:px-10 max-w-[1100px]">
        {/* Tab Navigation */}
        <div className="mb-6 mt-6">
          <ZTabs
            tabs={[
              { id: 'mantras', label: 'Mantras' },
              { id: 'essays', label: 'Essays' },
              { id: 'poems', label: 'Poems' },
              { id: 'paintings', label: 'Paintings' },
              { id: 'curated', label: 'Curated' },
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
            accent="#9B6DD4"
          />
        </div>

        {/* Mantras Tab */}
        {activeTab === 'mantras' && (
          <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <SortableContext items={mantras.map(m => m.id)} strategy={getSortingStrategy()}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2px]" data-testid="mantras-grid">
                  {gridItems.map((item) => item.type === 'painting' ? <PaintingVisual key={item.key} painting={item.painting} style={item.style} /> : <SortableMantraCard key={item.key} mantra={item.mantra} onOpen={openMantra} onEdit={startEditMantra} onDelete={confirmDeleteMantra} onTogglePublic={togglePublic} isDragging={activeId === item.mantra.id} />)}
                  <button data-testid="new-mantra-card" onClick={() => { setEditingMantra('new'); setMantraForm({ title: '', theme: '', subtitle: '', notes: '', attribution: '', quotes: [] }); }} className="text-left bg-transparent border border-dashed border-white/[0.06] p-6 md:p-7 flex items-center justify-center min-h-[160px] hover:border-[#6b7084] transition-colors group">
                    <div className="text-center"><Plus size={20} className="mx-auto mb-2 text-[#b0b4c0] group-hover:text-[#6b7084] transition-colors" /><span className="text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>New Mantra</span></div>
                  </button>
                </div>
              </SortableContext>
            </DndContext>

            {/* Enneagram footer */}
            <div className="mt-16 pb-12 pt-8 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-8">
              {[{ type: '7w8', label: 'The Trailblazer', desc: 'Adventurous, assertive, fiercely independent - chasing excitement while breaking through limits.' }, { type: '8w7', label: 'The Maverick', desc: 'Bold, high-energy, unstoppable - driven to seize freedom and bend the world to their will.' }].map(e => (
                <div key={e.type}><p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>Enneagram {e.type}</p><p className="text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>{e.label}</p><p className="text-sm italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>{e.desc}</p></div>
              ))}
            </div>
          </>
        )}

        {/* Essays Tab */}
        {activeTab === 'essays' && (
          <div>
            <div className="flex justify-end mb-6">
              <Button onClick={openNewEssay} className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-4 text-xs"><Plus size={14} className="mr-1.5" /> Essay</Button>
            </div>
            {essays.length === 0 ? (
              <p className="text-center py-16 text-[#b0b4c0]/50 font-light">No essays yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {essays.map(essay => (
                  <div key={essay.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 cursor-pointer hover:border-[#b0b4c0] hover:scale-[1.02] transition-all duration-300" style={{ position: 'relative' }}>
                    <OverflowMenu
                      publishToggle={{ isPublished: essay.is_public, onToggle: () => toggleEssayPublic(essay) }}
                      items={[
                        { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => openEssay(essay) },
                        { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => setDeleteConfirm({ open: true, type: 'essay', id: essay.id, name: essay.title || 'this essay' }), destructive: true },
                      ]}
                    />
                    <div onClick={() => openEssay(essay)}>
                      <p className="text-base text-[#f0f0f5] pr-8" style={{ fontFamily: "'Playfair Display', serif" }}>{essay.title}</p>
                      {essay.tagline && <p className="text-xs text-[#b0b4c0]/60 font-light italic mt-1">{essay.tagline}</p>}
                      <div className="flex items-center mt-4">
                        <span className="text-[10px] font-mono text-[#b0b4c0]/40">{formatDate(essay.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Poems Tab */}
        {activeTab === 'poems' && (
          <div>
            <div className="flex justify-end mb-6">
              <Button onClick={openNewPoem} className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-4 text-xs"><Plus size={14} className="mr-1.5" /> Poem</Button>
            </div>
            {poems.length === 0 ? (
              <p className="text-center py-16 text-[#b0b4c0]/50 font-light">No poems yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {poems.map(poem => (
                  <div key={poem.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 cursor-pointer hover:border-[#b0b4c0] hover:scale-[1.02] transition-all duration-300" style={{ position: 'relative' }}>
                    <OverflowMenu
                      publishToggle={{ isPublished: poem.is_public, onToggle: () => togglePoemPublic(poem) }}
                      items={[
                        { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => openPoem(poem) },
                        { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => setDeleteConfirm({ open: true, type: 'poem', id: poem.id, name: poem.title || 'this poem' }), destructive: true },
                      ]}
                    />
                    <div onClick={() => openPoem(poem)}>
                      <p className="text-base text-[#f0f0f5] pr-8" style={{ fontFamily: "'Playfair Display', serif" }}>{poem.title}</p>
                      <div className="flex items-center mt-4">
                        <span className="text-[10px] font-mono text-[#b0b4c0]/40">{formatDate(poem.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Paintings Tab */}
        {activeTab === 'paintings' && (
          <div>
            <div className="flex justify-end mb-6">
              <Button onClick={() => setShowPaintingUpload(true)} className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-4 text-xs"><Plus size={14} className="mr-1.5" /> Add Paintings</Button>
            </div>
            {cultPaintings.length === 0 ? (
              <p className="text-center py-16 text-[#b0b4c0]/50 font-light">No paintings yet</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={e => setPaintingActiveId(e.active.id)} onDragEnd={handlePaintingDragEnd}>
                <SortableContext items={cultPaintings.map(p => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[2px]">
                    {cultPaintings.map(painting => (
                      <SortablePaintingCard key={painting.id} painting={painting} onEdit={p => { setEditingPainting(p); setPaintingTitleForm(p.title || ''); }} onDelete={deleteCultPainting} onTogglePublic={togglePaintingPublic} onView={setViewingPainting} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        )}


        {/* Curated Tab */}
        {activeTab === 'curated' && (
          <div className="py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {curatedGuides.map(g => (
                <div key={g.slug} className="relative group cursor-pointer border border-white/[0.06] hover:border-[#b0b4c0] transition-all duration-300 overflow-hidden bg-white/[0.02]"
                  onClick={() => navigate(`/curated/${g.slug}`)}>
                  <OverflowMenu
                    publishToggle={{ isPublished: !!g.is_public, onToggle: () => toggleGuidePublic(g) }}
                    items={[
                      { icon: <Link2 size={16} strokeWidth={1.5} />, label: 'Copy Link', onClick: () => { navigator.clipboard.writeText(`${window.location.origin}/gallery/cult/curated/${g.slug}`); } },
                      { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => startEditGuide(g) },
                      { icon: <FileDown size={16} strokeWidth={1.5} />, label: 'Download PDF', onClick: () => window.open(`/curated/${g.slug}/print?auto=1`, '_blank', 'noopener,noreferrer') },
                      { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => setDeleteConfirm({ open: true, type: 'curated-guide', id: g.slug, name: g.title }), destructive: true },
                    ]}
                  />
                  <div className="h-48 relative overflow-hidden" style={{ background: '#0a0a0f' }}>
                    {g.hero_image_url && <img src={g.hero_image_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.20) 40%, transparent 70%)' }} />
                    <div style={{ position: 'absolute', bottom: 16, left: 20, right: 20 }}>
                      <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#f0f0f5' }}>{g.title}</p>
                      {g.subtitle && <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.50)', letterSpacing: '0.08em' }}>{g.subtitle}</p>}
                    </div>
                  </div>
                </div>
              ))}

              {/* New guide card */}
              <div
                className="flex items-center justify-center border border-dashed border-white/[0.06] hover:border-[#b0b4c0] transition-all duration-300 cursor-pointer bg-white/[0.02] h-48"
                onClick={() => setShowNewGuide(true)}>
                <div className="text-center">
                  <Plus size={24} className="mx-auto mb-2" style={{ color: '#6b7084' }} />
                  <p className="text-xs tracking-[0.15em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#6b7084' }}>New Guide</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Guide Dialog */}
        <Dialog open={showNewGuide} onOpenChange={v => { if (!v) { setShowNewGuide(false); setNewGuideTitle(''); } }}>
          <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="new-guide-desc">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>New Curated Guide</DialogTitle></DialogHeader>
            <p id="new-guide-desc" className="sr-only">Create a new curated guide</p>
            <div className="space-y-4 mt-2">
              <Input
                value={newGuideTitle}
                onChange={e => setNewGuideTitle(e.target.value)}
                placeholder="Guide title"
                className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                onKeyDown={e => { if (e.key === 'Enter' && newGuideTitle.trim()) createGuide(); }}
                autoFocus
              />
              <Button onClick={createGuide} disabled={!newGuideTitle.trim() || creatingGuide} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">
                {creatingGuide ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

        {/* Edit Guide Dialog */}
        <Dialog open={!!editingGuide} onOpenChange={v => { if (!v) setEditingGuide(null); }}>
          <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="edit-guide-desc">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Edit Guide</DialogTitle></DialogHeader>
            <p id="edit-guide-desc" className="sr-only">Edit curated guide details</p>
            <div className="space-y-4 mt-2">
              <Input
                value={guideEditForm.title}
                onChange={e => setGuideEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Title"
                className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                autoFocus
              />
              <Input
                value={guideEditForm.subtitle}
                onChange={e => setGuideEditForm(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Subtitle"
                className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
              />
              <Button onClick={saveGuideEdit} disabled={!guideEditForm.title.trim()} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Painting Upload Dialog */}
      <PaintingUploadDialog open={showPaintingUpload} onClose={() => setShowPaintingUpload(false)} onSave={loadCultPaintings} />

      {/* Painting Title Edit Dialog */}
      <Dialog open={!!editingPainting} onOpenChange={v => { if (!v) setEditingPainting(null); }}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="edit-painting-desc">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Edit Painting Title</DialogTitle></DialogHeader>
          <p id="edit-painting-desc" className="sr-only">Edit painting title</p>
          <div className="space-y-4 mt-2">
            <Input value={paintingTitleForm} onChange={e => setPaintingTitleForm(e.target.value)} placeholder="Title" className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
            <Button onClick={savePaintingTitle} disabled={!paintingTitleForm} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New/Edit Mantra Dialog */}
      <Dialog open={editingMantra === 'new'} onOpenChange={(v) => { if (!v) setEditingMantra(null); }}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]" aria-describedby="mantra-form-desc">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>New Mantra</DialogTitle></DialogHeader>
          <p id="mantra-form-desc" className="sr-only">Form to create a mantra</p>
          <div className="space-y-4 mt-2">
            <Textarea data-testid="mantra-title-input" placeholder="The mantra..." value={mantraForm.title} onChange={(e) => setMantraForm(p => ({ ...p, title: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 min-h-[80px] text-base" style={{ fontFamily: "'Playfair Display', serif" }} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="mantra-theme-input" placeholder="Theme (e.g. PURPOSE)" value={mantraForm.theme} onChange={(e) => setMantraForm(p => ({ ...p, theme: e.target.value.toUpperCase() }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs tracking-widest uppercase font-mono" />
              <Input data-testid="mantra-subtitle-input" placeholder="Subtitle (optional)" value={mantraForm.subtitle} onChange={(e) => setMantraForm(p => ({ ...p, subtitle: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs italic" />
            </div>
            <Input data-testid="mantra-attribution-input" placeholder="Attribution (if not yours, e.g. 'Nietzsche')" value={mantraForm.attribution} onChange={(e) => setMantraForm(p => ({ ...p, attribution: e.target.value }))} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
            {/* Voices section */}
            <div className="border-t border-white/[0.06] pt-3">
              <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>Voices</p>
              {(mantraForm.quotes || []).map((q, i) => (
                <div key={i} className="flex items-start gap-2 mb-2 bg-[#0a0a0f] rounded-lg p-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#f0f0f5]/70 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>"{q.text}"</p>
                    {q.author && <p className="text-[10px] text-[#b0b4c0]/50 mt-0.5">— {q.author}</p>}
                  </div>
                  <button onClick={() => setMantraForm(p => ({ ...p, quotes: p.quotes.filter((_, j) => j !== i) }))} className="text-[#b0b4c0]/30 hover:text-red-400 shrink-0 mt-0.5"><X size={12} /></button>
                </div>
              ))}
              <div className="space-y-2">
                <Textarea placeholder="Quote or supporting voice..." value={newVoiceText} onChange={(e) => setNewVoiceText(e.target.value)} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs min-h-[40px]" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
                <div className="flex gap-2">
                  <Input placeholder="Author (optional)" value={newVoiceAuthor} onChange={(e) => setNewVoiceAuthor(e.target.value)} className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/60 text-xs flex-1" />
                  <button disabled={!newVoiceText.trim()} onClick={() => { setMantraForm(p => ({ ...p, quotes: [...p.quotes, { text: newVoiceText.trim(), author: newVoiceAuthor.trim() }] })); setNewVoiceText(''); setNewVoiceAuthor(''); }} className="text-[10px] tracking-[0.15em] uppercase px-3 py-1 border border-dashed border-white/[0.06] hover:border-[#6b7084] transition-colors disabled:opacity-30" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>+ Add</button>
                </div>
              </div>
            </div>
            <Button data-testid="mantra-save-btn" disabled={!mantraForm.title} onClick={saveNewMantra} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Essay Edit Dialog */}
      <Dialog open={!!editingEssay} onOpenChange={(v) => { if (!v) setEditingEssay(null); }}>
        <DialogContent className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] max-w-2xl h-[90vh] flex flex-col p-0">
          {editingEssay?.id !== 'new' && (
            <div className="flex justify-end p-4 pb-0">
              <OverflowMenu
                publishToggle={{ isPublished: editingEssay?.is_public, onToggle: () => toggleEssayPublic(editingEssay) }}
                triggerStyle={{ position: 'static' }}
                items={[
                  { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => setDeleteConfirm({ open: true, type: 'essay', id: editingEssay?.id, name: editingEssay?.title || 'this essay' }), destructive: true },
                ]}
              />
            </div>
          )}
          <div className="flex-1 px-6 py-4 overflow-auto space-y-4">
            <Input value={essayForm.title} onChange={(e) => setEssayForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="bg-transparent border-none text-[#f0f0f5] text-xl p-0 focus-visible:ring-0" style={{ fontFamily: "'Playfair Display', serif" }} />
            <Input value={essayForm.tagline} onChange={(e) => setEssayForm(p => ({ ...p, tagline: e.target.value }))} placeholder="Tagline (optional)" className="bg-transparent border-none text-[#b0b4c0] font-light italic text-sm p-0 focus-visible:ring-0 placeholder:text-[#b0b4c0]/30" />
            <div className="border-b border-white/[0.06] my-2" />
            <Textarea value={essayForm.body} onChange={(e) => setEssayForm(p => ({ ...p, body: e.target.value }))} placeholder="Write..." className="bg-transparent border-none text-[#f0f0f5]/80 font-light leading-relaxed text-sm p-0 focus-visible:ring-0 min-h-[300px] resize-none" />
          </div>
          <div className="px-6 py-4 border-t border-white/[0.06]/50 flex items-center justify-end">
            <Button onClick={saveEssay} disabled={!essayForm.title || !essayForm.body} className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-6">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Poem Edit Dialog */}
      <Dialog open={!!editingPoem} onOpenChange={(v) => { if (!v) setEditingPoem(null); }}>
        <DialogContent className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] max-w-2xl h-[90vh] flex flex-col p-0">
          {editingPoem?.id !== 'new' && (
            <div className="flex justify-end p-4 pb-0">
              <OverflowMenu
                publishToggle={{ isPublished: editingPoem?.is_public, onToggle: () => togglePoemPublic(editingPoem) }}
                triggerStyle={{ position: 'static' }}
                items={[
                  { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => setDeleteConfirm({ open: true, type: 'poem', id: editingPoem?.id, name: editingPoem?.title || 'this poem' }), destructive: true },
                ]}
              />
            </div>
          )}
          <div className="flex-1 px-6 py-4 overflow-auto space-y-4">
            <Input value={poemForm.title} onChange={(e) => setPoemForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="bg-transparent border-none text-[#f0f0f5] text-xl p-0 focus-visible:ring-0" style={{ fontFamily: "'Playfair Display', serif" }} />
            <div className="border-b border-white/[0.06] my-2" />
            <Textarea value={poemForm.body} onChange={(e) => setPoemForm(p => ({ ...p, body: e.target.value }))} placeholder="Write..." className="bg-transparent border-none text-[#f0f0f5]/80 font-light leading-relaxed text-sm p-0 focus-visible:ring-0 min-h-[200px] resize-none italic whitespace-pre-wrap" style={{ fontFamily: "'Cormorant Garamond', serif" }} />
          </div>
          <div className="px-6 py-4 border-t border-white/[0.06]/50 flex items-center justify-end">
            <Button onClick={savePoem} disabled={!poemForm.title || !poemForm.body} className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-6">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })} onConfirm={handleDeleteConfirm} itemName={deleteConfirm.name} itemType={deleteConfirm.type} />

      {/* Painting fullscreen viewer */}
      {viewingPainting && (
        <div
          className="fixed inset-0 bg-[#0a0a0f]/95 z-[100] flex items-center justify-center cursor-pointer"
          onClick={() => setViewingPainting(null)}
        >
          <button onClick={() => setViewingPainting(null)} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-10">
            <X size={28} />
          </button>
          <img
            src={viewingPainting.url}
            alt={viewingPainting.title || ''}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          {viewingPainting.title && (
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-sm text-[#b0b4c0]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.05em' }}>
              {viewingPainting.title}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
